import {
  Component,
  computed,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessageService } from 'primeng/api';
import {
  INHERIT_MATERIAL_ID,
  INHERIT_MATERIAL_LABEL,
} from '../../../../models/quote-item-data.model';
import { QuoteRevisionItem } from '../../../../models/quote-revision-item.model';
import { MaterialsService } from '../../../materials.service';
import { QuoteRevisionItemService } from '../../../services/quote-revision-item.service';
import { MaterialSelectComponent } from '../../../shared/material-select/material-select.component';
import {
  TaxCodeOption,
  TaxCodeSelectComponent,
} from '../../../tax-code-select/tax-code-select.component';

export type BulkEditPreviewFieldKey =
  'markup' | 'tax' | 'material' | 'batchSize';

export interface BulkEditPreviewItem {
  persistentId: string;
  name: string;
  currentDisplay: string;
}

export interface BulkEditPreviewField {
  key: BulkEditPreviewFieldKey;
  label: string;
  changeToDisplay: string;
  items: BulkEditPreviewItem[];
}

interface BulkEditPreviewData {
  affectedCount: number;
  fields: BulkEditPreviewField[];
}

interface BulkEditPatchFlags {
  markup: boolean;
  tax: boolean;
  material: boolean;
  batchSize: boolean;
}

interface BulkEditPatch {
  markup_percentage?: string;
  tax_percentage?: number | null;
  material_id?: number | null;
  inherit_material?: boolean;
  batch_size?: number | null;
}

@Component({
  selector: 'app-quote-revision-item-bulk-edit-dialog',
  imports: [
    FormsModule,
    ButtonModule,
    CheckboxModule,
    DialogModule,
    InputNumberModule,
    MaterialSelectComponent,
    TaxCodeSelectComponent,
  ],
  templateUrl: './quote-revision-item-bulk-edit-dialog.component.html',
})
export class QuoteRevisionItemBulkEditDialogComponent {
  private revisionItemService = inject(QuoteRevisionItemService);
  private messageService = inject(MessageService);
  private materialsService = inject(MaterialsService);
  private taxCodeSelect = viewChild(TaxCodeSelectComponent);

  private readonly materials = toSignal(
    this.materialsService.activeMaterials$,
    {
      initialValue: [],
    },
  );

  visible = model(false);
  selectedItems = input.required<QuoteRevisionItem[]>();
  tradeStudyToolFeaturesEnabled = input(false);
  saved = output<void>();

  saving = false;
  previewExpanded = true;

  updateMarkup = signal(false);
  markupPercent = signal<number | null>(null);
  markupValueProvided = signal(false);

  updateTax = signal(false);
  taxPercentage = signal<number | null>(null);

  updateMaterial = signal(false);
  materialId = signal<number | null>(null);

  updateBatchSize = signal(false);
  batchSize = signal<number | null>(null);

  readonly previewData = computed((): BulkEditPreviewData => {
    const selectedItems = this.selectedItems();
    const tradeStudyEnabled = this.tradeStudyToolFeaturesEnabled();
    const updateMarkup = this.updateMarkup() && !tradeStudyEnabled;
    const updateTax = this.updateTax() && !tradeStudyEnabled;
    const updateMaterial = this.updateMaterial();
    const updateBatchSize = this.updateBatchSize();

    if (!updateMarkup && !updateTax && !updateMaterial && !updateBatchSize) {
      return { affectedCount: 0, fields: [] };
    }

    const markupValueProvided = this.markupValueProvided();
    const markupPercent = this.markupPercent();
    const taxPercentage = this.taxPercentage();
    const materialId = this.materialId();
    const batchSize = this.batchSize();
    const materials = this.materials();
    const taxOptions = this.taxCodeSelect()?.taxCodeOptions() ?? [];

    let affectedCount = 0;
    for (const item of selectedItems) {
      if (
        this.hasAnyPatchFlag(
          this.getPatchFlags(item, {
            tradeStudyEnabled,
            updateMarkup,
            updateTax,
            updateMaterial,
            updateBatchSize,
            markupValueProvided,
            markupPercent,
          }),
        )
      ) {
        affectedCount++;
      }
    }

    const fields: BulkEditPreviewField[] = [];

    if (updateMarkup) {
      fields.push({
        key: 'markup',
        label: 'Markup Percentage',
        changeToDisplay: markupValueProvided
          ? this.formatMarkupDisplay(markupPercent)
          : '—',
        items: selectedItems
          .filter((item) => !this.hasChildren(item))
          .map((item) => ({
            persistentId: item.persistent_id,
            name: item.name,
            currentDisplay: this.formatMarkupDisplay(item.markup_percentage),
          })),
      });
    }

    if (updateTax) {
      fields.push({
        key: 'tax',
        label: 'Tax Code',
        changeToDisplay: this.formatTaxDisplay(taxPercentage, taxOptions),
        items: selectedItems
          .filter((item) => !this.hasChildren(item))
          .map((item) => ({
            persistentId: item.persistent_id,
            name: item.name,
            currentDisplay: this.formatTaxDisplay(
              item.tax_percentage != null ? Number(item.tax_percentage) : null,
              taxOptions,
            ),
          })),
      });
    }

    if (updateMaterial) {
      fields.push({
        key: 'material',
        label: 'Material',
        changeToDisplay: this.formatMaterialSelectionDisplay(
          materialId,
          materials,
        ),
        items: selectedItems.map((item) => ({
          persistentId: item.persistent_id,
          name: item.name,
          currentDisplay: this.formatMaterialCurrentDisplay(item),
        })),
      });
    }

    if (updateBatchSize) {
      const hasBatchSize = batchSize != null && batchSize > 0;
      fields.push({
        key: 'batchSize',
        label: 'Batch Size',
        changeToDisplay: hasBatchSize ? String(batchSize) : 'None',
        items: selectedItems.map((item) => ({
          persistentId: item.persistent_id,
          name: item.name,
          currentDisplay:
            item.batch_size != null ? String(item.batch_size) : '—',
        })),
      });
    }

    return { affectedCount, fields };
  });

  readonly canSave = computed(() => {
    const updateMarkup =
      this.updateMarkup() && !this.tradeStudyToolFeaturesEnabled();
    const updateTax = this.updateTax() && !this.tradeStudyToolFeaturesEnabled();
    if (
      !updateMarkup &&
      !updateTax &&
      !this.updateMaterial() &&
      !this.updateBatchSize()
    ) {
      return false;
    }
    return !(updateMarkup && !this.markupValueProvided());
  });

  readonly showPreviewChanges = computed(() => {
    const updateMarkup =
      this.updateMarkup() && !this.tradeStudyToolFeaturesEnabled();
    const updateTax = this.updateTax() && !this.tradeStudyToolFeaturesEnabled();
    return (
      updateMarkup ||
      updateTax ||
      this.updateMaterial() ||
      this.updateBatchSize()
    );
  });

  onUpdateMarkupChange(enabled: boolean): void {
    this.updateMarkup.set(enabled);
    if (!enabled) {
      this.markupPercent.set(null);
      this.markupValueProvided.set(false);
    }
  }

  onMarkupPercentChange(value: number | null): void {
    this.markupPercent.set(value);
    this.markupValueProvided.set(value != null);
  }

  onUpdateTaxChange(enabled: boolean): void {
    this.updateTax.set(enabled);
    if (!enabled) {
      this.taxPercentage.set(null);
    }
  }

  onUpdateMaterialChange(enabled: boolean): void {
    this.updateMaterial.set(enabled);
    if (!enabled) {
      this.materialId.set(null);
    }
  }

  togglePreviewExpanded(): void {
    this.previewExpanded = !this.previewExpanded;
  }

  get selectedItemsWithChildrenCount(): number {
    return this.selectedItems().filter((item) => this.hasChildren(item)).length;
  }

  get markupTaxApplicableItemCount(): number {
    return this.selectedItems().filter((item) => !this.hasChildren(item))
      .length;
  }

  get markupTaxExclusionMessage(): string | null {
    if (this.tradeStudyToolFeaturesEnabled()) {
      return null;
    }
    const count = this.selectedItemsWithChildrenCount;
    if (count === 0) {
      return null;
    }
    if (count === 1) {
      return 'Markup and tax changes do not apply to the selected item that has subitems.';
    }
    return `Markup and tax changes do not apply to ${count} of the selected items that have subitems.`;
  }

  onDialogShow(): void {
    this.resetForm();
  }

  close(): void {
    this.visible.set(false);
  }

  private resetForm(): void {
    this.updateMarkup.set(false);
    this.markupPercent.set(null);
    this.markupValueProvided.set(false);
    this.updateTax.set(false);
    this.taxPercentage.set(null);
    this.updateMaterial.set(false);
    this.materialId.set(null);
    this.updateBatchSize.set(false);
    this.batchSize.set(null);
    this.saving = false;
    this.previewExpanded = true;
  }

  private hasChildren(item: QuoteRevisionItem): boolean {
    return (item.children?.length ?? 0) > 0;
  }

  private getPatchFlags(
    item: QuoteRevisionItem,
    context: {
      tradeStudyEnabled: boolean;
      updateMarkup: boolean;
      updateTax: boolean;
      updateMaterial: boolean;
      updateBatchSize: boolean;
      markupValueProvided: boolean;
      markupPercent: number | null;
    },
  ): BulkEditPatchFlags {
    const canUpdateMarkupAndTax =
      !context.tradeStudyEnabled && !this.hasChildren(item);

    return {
      markup:
        context.updateMarkup &&
        canUpdateMarkupAndTax &&
        context.markupValueProvided &&
        context.markupPercent != null,
      tax: context.updateTax && canUpdateMarkupAndTax,
      material: context.updateMaterial,
      batchSize: context.updateBatchSize,
    };
  }

  private hasAnyPatchFlag(flags: BulkEditPatchFlags): boolean {
    return flags.markup || flags.tax || flags.material || flags.batchSize;
  }

  private formatMarkupDisplay(
    value: number | string | null | undefined,
  ): string {
    if (value == null || value === '') {
      return '—';
    }
    const num = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(num)) {
      return '—';
    }
    return `${num}%`;
  }

  private formatTaxDisplay(
    percentage: number | null,
    taxOptions: TaxCodeOption[],
  ): string {
    if (percentage == null) {
      return 'None';
    }
    const match = taxOptions.find(
      (opt) =>
        opt.value != null &&
        opt.percentage != null &&
        Number(opt.percentage) === Number(percentage),
    );
    return match?.label ?? `${percentage}%`;
  }

  private formatMaterialCurrentDisplay(item: QuoteRevisionItem): string {
    if (item.inherit_material) {
      return INHERIT_MATERIAL_LABEL;
    }
    return item.material_name?.trim() || 'None';
  }

  private formatMaterialSelectionDisplay(
    materialId: number | null,
    materials: { id: number; name: string }[],
  ): string {
    if (materialId === INHERIT_MATERIAL_ID) {
      return INHERIT_MATERIAL_LABEL;
    }
    if (materialId == null) {
      return 'None';
    }
    return (
      materials.find((material) => material.id === materialId)?.name ??
      String(materialId)
    );
  }

  private buildPatch(item: QuoteRevisionItem): BulkEditPatch {
    const tradeStudyEnabled = this.tradeStudyToolFeaturesEnabled();
    const flags = this.getPatchFlags(item, {
      tradeStudyEnabled,
      updateMarkup: this.updateMarkup() && !tradeStudyEnabled,
      updateTax: this.updateTax() && !tradeStudyEnabled,
      updateMaterial: this.updateMaterial(),
      updateBatchSize: this.updateBatchSize(),
      markupValueProvided: this.markupValueProvided(),
      markupPercent: this.markupPercent(),
    });

    const patch: BulkEditPatch = {};

    if (flags.markup) {
      patch.markup_percentage = this.markupPercent()!.toFixed(2);
    }

    if (flags.tax) {
      patch.tax_percentage = this.taxPercentage() ?? null;
    }

    if (flags.material) {
      const materialId = this.materialId();
      if (materialId === INHERIT_MATERIAL_ID) {
        patch.material_id = null;
        patch.inherit_material = true;
      } else {
        patch.material_id = materialId;
        patch.inherit_material = false;
      }
    }

    if (flags.batchSize) {
      const batchSize = this.batchSize();
      const hasBatchSize = batchSize != null && batchSize > 0;
      patch.batch_size = hasBatchSize ? batchSize : null;
    }

    return patch;
  }

  async save(): Promise<void> {
    if (!this.canSave()) {
      return;
    }

    this.saving = true;

    try {
      const updates = this.selectedItems()
        .map((item) => ({
          persistent_id: item.persistent_id,
          ...this.buildPatch(item),
        }))
        .filter((update) => Object.keys(update).length > 1);

      if (updates.length === 0) {
        this.messageService.add({
          severity: 'info',
          summary: 'No changes applied',
          detail:
            'Markup and tax changes do not apply to items that have subitems. Select items without subitems, or update material or batch size instead.',
        });
        return;
      }

      await this.revisionItemService.bulkUpdate(updates);
      this.messageService.add({
        severity: 'success',
        summary: 'Bulk edit saved',
        detail: `Updated ${updates.length} item(s).`,
      });
      this.saved.emit();
      this.visible.set(false);
    } finally {
      this.saving = false;
      this.resetForm();
    }
  }
}
